import { datadogRum } from "@datadog/browser-rum";

import type { DeployedEnvironmentName } from "@/helpers/environment";

import { scrubSensitiveQueryParams } from "./utils";

export default function init(
  releaseId: string,
  environment: DeployedEnvironmentName,
) {
  datadogRum.init({
    applicationId: "656f2bed-d7e7-46ab-a077-178e5ad6d533",
    clientToken: "pub0f2f3d94b03f595f9d0231c44e66522e",
    site: "datadoghq.eu",
    service: "weld-app",
    version: releaseId,
    sessionSampleRate: 100,
    sessionReplaySampleRate: environment !== "production" ? 0 : 100,
    // Frustration signals are collected as part of this, so the separate
    // `trackFrustrations` option that used to sit here no longer exists.
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    env: environment,
    // Defense-in-depth: redact single-use tokens (e.g. the GitHub install
    // choice token in `?githubInstallState=`) from any captured view, resource
    // or referrer URL before the event is sent.
    beforeSend(event) {
      if (event.view?.url) {
        event.view.url = scrubSensitiveQueryParams(event.view.url);
      }
      if (event.view?.referrer) {
        event.view.referrer = scrubSensitiveQueryParams(event.view.referrer);
      }
      if (event.type === "resource" && event.resource?.url) {
        event.resource.url = scrubSensitiveQueryParams(event.resource.url);
      }
      return true;
    },
  });

  /**
   * Start session replay recording for 50% of users.
   * The reason for manually performing a draw here instead of setting
   * `sessionReplaySampleRate` to 50% is that we want to be able to explicitly
   * start session recording based based on other factors (e.g. new user going through signup flow).
   * This is not possible unless `sessionReplaySampleRate` is set to 100%.
   */
  const shouldStartSessionReplayRecording = Math.random() < 0.5;
  if (shouldStartSessionReplayRecording) {
    // Deferred to idle while `init` above stays synchronous, so RUM still sees
    // the whole session: it is the recorder that is expensive, serialising the
    // DOM and then observing every mutation React makes while the app boots.
    // The cost is that a replay begins after the boot rather than during it.
    startWhenIdle(() => datadogRum.startSessionReplayRecording());
  }
}

function startWhenIdle(task: () => void) {
  if (typeof requestIdleCallback === "function") {
    // Capped, or a main thread that never goes idle would never start it.
    requestIdleCallback(task, { timeout: 3000 });
  } else {
    setTimeout(task, 1000);
  }
}
